<template>
  <div>
    <b-modal
      id="viewingAppointmentParticipantsModal"
      ref="viewingAppointmentParticipantsModal"
      size="lg"
      no-close-on-backdrop
      hide-footer
      :title="modalTitle"
      @hidden="onHidden">
      <div class="row align-items-center mb-3">
        <div class="col-auto">
          <label
            for="viewingAppointmentMaxParticipants"
            class="mb-0">
            {{ $t('message.marketingObjects.viewingAppointments.maxParticipants') + ':' }}
          </label>
        </div>
        <div class="col-4 col-md-3">
          <coozzy-form-input
            id="viewingAppointmentMaxParticipants"
            v-model="maxParticipantsInput"
            type="number"
            :min="1"
            :is-read-only="!isScheduled || updatingMaxParticipants" />
        </div>
        <div
          v-if="isScheduled"
          class="col-auto">
          <coozzy-button
            design="green"
            size="small"
            :disabled="updatingMaxParticipants"
            @click="submitMaxParticipants">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </div>
      <h6 class="mb-3">
        {{ $t('message.marketingObjects.viewingAppointments.columns.participants') }}
      </h6>
      <b-table
        bordered
        hover
        responsive="true"
        :fields="fields"
        :items="participants"
        class="mb-0"
        thead-class="thead-light">
        <template #cell(name)="data">
          <a
            href="#"
            class="participant-name-link"
            @click.prevent="openRequest(data.item)">
            {{ participantName(data.item) }}
          </a>
        </template>
        <template #cell(communication)="data">
          <div v-if="data.item.phone">
            {{ data.item.phone }}
          </div>
          <div v-if="data.item.email">
            {{ data.item.email }}
          </div>
          <span v-if="!data.item.phone && !data.item.email">-</span>
        </template>
        <template #cell(action)="data">
          <coozzy-button
            design="transparent"
            class="p-0"
            @click="openCancelConfirm(data.item)">
            <coozzy-delete-icon />
          </coozzy-button>
        </template>
      </b-table>
      <div
        v-if="!participants.length"
        class="text-muted mt-2">
        —
      </div>
    </b-modal>

    <b-modal
      id="viewingAppointmentCancelParticipantModal"
      ref="viewingAppointmentCancelParticipantModal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.marketingObjects.viewingAppointments.cancelParticipant.title')">
      <div class="col pl-0">
        <coozzy-button
          size="small"
          design="default"
          :disabled="unregistering"
          @click="hideCancelConfirm">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          design="prop-green"
          :disabled="unregistering"
          @click="unregisterWithoutMail">
          {{ $t('message.generic.rejectWithoutMail') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0 mr-3"
          design="prop-green"
          :disabled="unregistering"
          @click="unregisterWithMail">
          {{ $t('message.generic.rejectWithMail') }}
        </coozzy-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters, mapMutations } from 'vuex'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon'
import CalendarSchedulerApi from '@/misc/apis/CalendarSchedulerApi'
import { dateUtils } from '@/mixins/dateUtils'
import { mail } from '@/mixins/mail'
import { padTimePart } from '@/misc/utils/viewingAppointmentSlotUtils'
import { SchedulerAppointmentStatus } from '@/types/api'

export default {
  name: 'ViewingAppointmentParticipantsModal',
  components: {
    CoozzyButton,
    CoozzyDeleteIcon
  },
  mixins: [dateUtils, mail],
  props: {
    objectId: {
      type: String,
      required: true
    },
    appointment: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      pendingParticipant: null,
      awaitingUnregisterMailSend: false,
      sentMailsLengthAtAwait: 0,
      pendingUnregisterRequestId: '',
      pendingUnregisterParticipant: null,
      unregistering: false,
      maxParticipantsInput: '',
      updatingMaxParticipants: false,
      fields: [
        {
          key: 'name',
          label: this.$t('message.marketingObjects.viewingAppointments.participantsModal.name'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'communication',
          label: this.$t('message.marketingObjects.viewingAppointments.participantsModal.communication'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'action',
          label: '',
          thClass: 'align-middle text-right',
          tdClass: 'align-middle text-right'
        }
      ]
    }
  },
  computed: {
    ...mapGetters('message', ['getSentMails']),
    participants() {
      return this.appointment?.participants || []
    },
    isScheduled() {
      return this.appointment?.status === SchedulerAppointmentStatus.APPOINTMENT_STATUS_SCHEDULED
    },
    modalTitle() {
      if (!this.appointment) {
        return ''
      }
      return this.$t('message.marketingObjects.viewingAppointments.participantsModal.title', {
        date: this.formatAppointmentDate(this.appointment),
        from: this.formatTime(this.appointment.startTime),
        to: this.formatTime(this.appointment.endTime)
      })
    }
  },
  watch: {
    appointment(newVal) {
      if (newVal) {
        this.maxParticipantsInput = this.formatMaxParticipants(newVal.maxParticipants)
        this.updatingMaxParticipants = false
        this.$nextTick(() => {
          this.$refs.viewingAppointmentParticipantsModal?.show()
        })
      }
    },
    getSentMails(mails) {
      this.tryCompleteUnregisterMailSend(mails)
    }
  },
  mounted() {
    this.$root.$on('bv::modal::hidden', this.onSendMailModalHidden)
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::hidden', this.onSendMailModalHidden)
  },
  methods: {
    ...mapMutations('message', [
      'setSendMailContacts',
      'setSendMailPreselectedContact',
      'setSendMailReferenceIds',
      'setSendMailEmailToReply',
      'setSubject',
      'setAdditionalText',
      'setRenderWithObjectId',
      'setUseBcc',
      'setSendMailSecondaryTitle'
    ]),
    show() {
      this.$refs.viewingAppointmentParticipantsModal?.show()
    },
    hide() {
      this.$refs.viewingAppointmentParticipantsModal?.hide()
    },
    onHidden() {
      this.pendingParticipant = null
      this.maxParticipantsInput = ''
      this.updatingMaxParticipants = false
      this.$emit('closed')
    },
    formatMaxParticipants(maxParticipants) {
      if (maxParticipants === undefined || maxParticipants === null || maxParticipants === '' || maxParticipants === 0) {
        return ''
      }
      return String(maxParticipants)
    },
    formatAppointmentDate(appointment) {
      if (!appointment?.date) {
        return ''
      }
      return this.objectToStringDate(appointment.date)
    },
    formatTime(time) {
      if (!time) {
        return ''
      }
      return padTimePart(time.hours || 0) + ':' + padTimePart(time.minutes || 0)
    },
    participantName(participant) {
      return [participant.firstName, participant.lastName].filter(Boolean).join(' ').trim() || '-'
    },
    submitMaxParticipants() {
      if (!this.appointment?.id || !this.isScheduled) {
        return
      }
      const raw = this.maxParticipantsInput
      let maxParticipants
      if (raw === '' || raw === null || raw === undefined) {
        maxParticipants = undefined
      } else {
        maxParticipants = parseInt(raw, 10)
      }
      if (Number.isNaN(maxParticipants)) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.maxAtLeastOne'), { type: 'error' })
        return
      }
      if (maxParticipants !== undefined && maxParticipants < 1) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.maxAtLeastOne'), { type: 'error' })
        return
      }
      const currentParticipants = (this.appointment.participants || []).length
      if (maxParticipants !== undefined && maxParticipants < currentParticipants) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.maxBelowRegistered'), { type: 'error' })
        return
      }
      this.updatingMaxParticipants = true
      CalendarSchedulerApi.updateMaxParticipants(this.appointment.id, maxParticipants)
        .then(response => {
          this.updatingMaxParticipants = false
          const updated = response.appointment
          if (updated) {
            this.maxParticipantsInput = this.formatMaxParticipants(updated.maxParticipants)
            Vue.toasted.show(this.$t('message.successMessages.changeSaved'), { type: 'success' })
            this.$emit('max-participants-updated', {
              appointmentId: updated.id,
              appointment: updated
            })
          }
        })
        .catch(e => {
          console.log(e)
          this.updatingMaxParticipants = false
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        })
    },
    openRequest(participant) {
      if (!participant?.requestId) {
        return
      }
      this.hide()
      this.$emit('open-request', participant.requestId)
    },
    openCancelConfirm(participant) {
      this.pendingParticipant = participant
      this.$refs.viewingAppointmentCancelParticipantModal?.show()
    },
    hideCancelConfirm() {
      this.$refs.viewingAppointmentCancelParticipantModal?.hide()
      this.pendingParticipant = null
    },
    unregisterParticipant(participant = this.pendingParticipant || this.pendingUnregisterParticipant) {
      if (!this.appointment?.id || !participant?.id) {
        return Promise.resolve(false)
      }
      this.unregistering = true
      return CalendarSchedulerApi.unregisterParticipant(this.appointment.id, participant.id)
        .then(() => {
          this.unregistering = false
          this.$emit('participant-unregistered', {
            appointmentId: this.appointment.id,
            participantId: participant.id
          })
          this.pendingUnregisterParticipant = null
          return true
        })
        .catch(e => {
          console.log(e)
          this.unregistering = false
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
          return false
        })
    },
    unregisterWithoutMail() {
      this.unregisterParticipant()
        .then(ok => {
          if (ok) {
            this.hideCancelConfirm()
          }
        })
    },
    unregisterWithMail() {
      const participant = this.pendingParticipant
      if (!participant) {
        return
      }
      if (!participant.email) {
        Vue.toasted.show(this.$t('message.loadingErrors.contactHasNoEmail'), { type: 'error' })
        return
      }
      this.pendingUnregisterParticipant = participant
      this.hideCancelConfirm()
      const contact = {
        id: participant.contactId || participant.id,
        firstName: participant.firstName || '',
        lastName: participant.lastName || '',
        name: this.participantName(participant),
        email: participant.email || ''
      }
      this.awaitingUnregisterMailSend = true
      this.sentMailsLengthAtAwait = this.getSentMails.length
      this.pendingUnregisterRequestId = participant.requestId || ''
      this.setSendMailContacts([contact])
      this.setSendMailPreselectedContact(contact)
      this.setSendMailReferenceIds(participant.requestId ? [participant.requestId] : [])
      this.setSendMailEmailToReply(null)
      this.setUseBcc(false)
      this.setSendMailSecondaryTitle(false)
      this.setSubject('')
      this.setAdditionalText('')
      this.setRenderWithObjectId(this.objectId)
      this.$nextTick(() => {
        this.callShowSendMailModal()
      })
    },
    clearAwaitingUnregisterMailSend() {
      this.awaitingUnregisterMailSend = false
      this.sentMailsLengthAtAwait = 0
      this.pendingUnregisterRequestId = ''
      this.pendingUnregisterParticipant = null
    },
    isUnregisterMailSent(mail, requestId, participantEmail) {
      if (!mail || mail.inbound === true) {
        return false
      }
      if (requestId && (mail.referenceIds || []).includes(requestId)) {
        return true
      }
      if (!participantEmail) {
        return false
      }
      const normalizedEmail = participantEmail.trim().toLowerCase()
      return (mail.to || []).some(recipient =>
        (recipient.email || '').trim().toLowerCase() === normalizedEmail
      )
    },
    tryCompleteUnregisterMailSend(mails) {
      if (!this.awaitingUnregisterMailSend || !Array.isArray(mails)) {
        return false
      }
      if (mails.length <= this.sentMailsLengthAtAwait) {
        return false
      }
      const participant = this.pendingUnregisterParticipant
      if (!participant) {
        return false
      }
      const requestId = this.pendingUnregisterRequestId
      const participantEmail = participant.email || ''
      const newMails = mails.slice(this.sentMailsLengthAtAwait)
      const mailSent = newMails.some(mail => this.isUnregisterMailSent(mail, requestId, participantEmail))
      if (!mailSent) {
        return false
      }
      this.awaitingUnregisterMailSend = false
      this.sentMailsLengthAtAwait = 0
      this.pendingUnregisterRequestId = ''
      this.unregisterParticipant(participant).then(ok => {
        if (!ok) {
          this.pendingUnregisterParticipant = participant
        }
      })
      return true
    },
    onSendMailModalHidden(bvEvent, modalId) {
      if (modalId !== 'modal-send-mail' || !this.awaitingUnregisterMailSend) {
        return
      }
      this.$nextTick(() => {
        if (!this.awaitingUnregisterMailSend) {
          return
        }
        if (this.tryCompleteUnregisterMailSend(this.getSentMails)) {
          return
        }
        this.clearAwaitingUnregisterMailSend()
      })
    }
  }
}
</script>

<style scoped>
.participant-name-link {
  color: #007bff;
  text-decoration: none;
}
.participant-name-link:hover {
  text-decoration: underline;
}
</style>
